import { useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface UpgradeModalProps {
  cartaId: string;
  prazoAnos: number;
  onClose: () => void;
  onUpgradeSuccess: () => void;
}

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

const UpgradeModal = ({ cartaId, prazoAnos, onClose, onUpgradeSuccess }: UpgradeModalProps) => {
  const [selectedMetodo, setSelectedMetodo] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [copiedPix, setCopiedPix] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  const formatCpfCnpj = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCpfCnpj(formatCpfCnpj(e.target.value));
    setError('');
  };

  const copyPixCode = () => {
    if (paymentData?.pix?.payload) {
      navigator.clipboard.writeText(paymentData.pix.payload);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 3000);
    }
  };

  const checkPaymentStatus = async (paymentId: string) => {
    try {
      const { data: pagamento } = await supabase
        .from('pagamentos')
        .select('status')
        .eq('stripe_session_id', paymentId)
        .maybeSingle();

      if (pagamento && pagamento.status === 'pago') {
        setPaymentConfirmed(true);
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        // Atualizar carta para premium
        await supabase
          .from('cartas')
          .update({ plano: 'premium', status: 'ativa' })
          .eq('id', cartaId);

        setTimeout(() => {
          onUpgradeSuccess();
        }, 2000);
      }
    } catch (err) {
      console.error('Erro ao verificar pagamento:', err);
    }
  };

  const startPaymentPolling = (paymentId: string) => {
    setIsCheckingPayment(true);
    const interval = setInterval(() => {
      checkPaymentStatus(paymentId);
    }, 5000);
    setPollingInterval(interval);
    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setIsCheckingPayment(false);
      }
    }, 30 * 60 * 1000);
  };

  const handleUpgrade = async () => {
    if (!selectedMetodo) {
      setError('Selecione um m\u00e9todo de pagamento');
      return;
    }
    if (!cpfCnpj.trim() || cpfCnpj.replace(/\D/g, '').length < 11) {
      setError('Informe um CPF ou CNPJ v\u00e1lido');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usu\u00e1rio n\u00e3o autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('nome, email')
        .eq('id', user.id)
        .maybeSingle();

      const metodoPagamentoMap: Record<string, string> = {
        'pix': 'PIX',
        'boleto': 'BOLETO',
        'cartao': 'CREDIT_CARD'
      };

      const paymentResponse = await fetch(`${supabaseUrl}/functions/v1/asaas-create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          nome: profile?.nome || user.email,
          email: profile?.email || user.email,
          cpfCnpj: cpfCnpj.replace(/\D/g, ''),
          valor: 97.00,
          descricao: `Upgrade Premium - Carta do Eu Futuro (${prazoAnos} ${prazoAnos === 1 ? 'ano' : 'anos'})`,
          metodo_pagamento: metodoPagamentoMap[selectedMetodo],
          user_id: user.id,
        }),
      });

      if (!paymentResponse.ok) {
        const errData = await paymentResponse.json();
        throw new Error(errData.error || 'Erro ao processar pagamento');
      }

      const paymentResult = await paymentResponse.json();
      setPaymentData(paymentResult.payment);
      setShowPayment(true);

      if (selectedMetodo === 'pix') {
        startPaymentPolling(paymentResult.payment.id);
      }
    } catch (err: any) {
      setError(err.message || 'Erro inesperado. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Tela de pagamento confirmado
  if (paymentConfirmed) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6">
        <div className="bg-luxury-dark border border-gold-500/30 rounded-2xl max-w-md w-full p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <i className="ri-check-line text-4xl text-white"></i>
          </div>
          <h3 className="font-['Playfair_Display'] text-2xl text-white mb-3">
            Upgrade Confirmado! 🎉
          </h3>
          <p className="font-['Montserrat'] text-sm text-white/60 mb-6">
            Sua carta agora \u00e9 Premium! Voc\u00ea receber\u00e1 por email na data escolhida.
          </p>
          <div className="flex justify-center">
            <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  // Tela de pagamento PIX/Boleto/Cartão
  if (showPayment && paymentData) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6 overflow-y-auto">
        <div className="bg-luxury-dark border border-gold-500/30 rounded-2xl max-w-lg w-full p-8 my-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-['Playfair_Display'] text-xl text-white">Pagamento do Upgrade</h3>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center cursor-pointer">
              <i className="ri-close-line text-white/60"></i>
            </button>
          </div>

          {selectedMetodo === 'pix' && isCheckingPayment && (
            <div className="mb-6 bg-gold-500/10 border border-gold-500/20 rounded-xl p-4 flex items-center gap-3 animate-pulse">
              <div className="w-8 h-8 border-3 border-gold-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
              <div>
                <p className="font-['Montserrat'] text-xs font-semibold text-gold-400">Verificando pagamento...</p>
                <p className="font-['Montserrat'] text-xs text-white/40">Redirecionamento autom\u00e1tico ap\u00f3s confirma\u00e7\u00e3o</p>
              </div>
            </div>
          )}

          {/* PIX */}
          {selectedMetodo === 'pix' && paymentData.pix && (
            <div>
              <div className="text-center mb-6">
                <p className="font-['Montserrat'] text-xs text-white/40 mb-1">Valor</p>
                <p className="font-['Playfair_Display'] text-3xl text-gold-400 font-bold">R$ 97,00</p>
              </div>
              <div className="bg-white p-6 rounded-xl mb-6 flex justify-center">
                <img src={`data:image/png;base64,${paymentData.pix.qrCode}`} alt="QR Code PIX" className="w-48 h-48" />
              </div>
              <div className="bg-white/5 rounded-xl p-4 mb-4">
                <p className="font-['Montserrat'] text-xs text-white/40 mb-2 text-center">C\u00f3digo PIX Copia e Cola</p>
                <div className="bg-black/40 rounded-lg p-3 break-all font-mono text-xs text-white/60 mb-3">
                  {paymentData.pix.payload}
                </div>
                <button onClick={copyPixCode} className="w-full bg-gradient-to-r from-gold-500 to-gold-600 text-luxury-black font-['Montserrat'] font-semibold text-sm py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className={`${copiedPix ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                  {copiedPix ? 'Copiado!' : 'Copiar c\u00f3digo PIX'}
                </button>
              </div>
            </div>
          )}

          {/* Boleto */}
          {selectedMetodo === 'boleto' && paymentData.boleto && (
            <div className="text-center">
              <p className="font-['Playfair_Display'] text-3xl text-gold-400 font-bold mb-6">R$ 97,00</p>
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <p className="font-['Montserrat'] text-xs text-white/40 mb-2">Linha digit\u00e1vel</p>
                <div className="bg-black/40 rounded-lg p-3 break-all font-mono text-xs text-white/60">
                  {paymentData.boleto.identificationField}
                </div>
              </div>
              <a href={paymentData.invoiceUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-gradient-to-r from-gold-500 to-gold-600 text-luxury-black font-['Montserrat'] font-semibold text-sm py-3 rounded-lg flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-download-line"></i>
                Baixar boleto
              </a>
            </div>
          )}

          {/* Cartão */}
          {selectedMetodo === 'cartao' && (
            <div className="text-center">
              <p className="font-['Playfair_Display'] text-3xl text-gold-400 font-bold mb-6">R$ 97,00</p>
              <a href={paymentData.invoiceUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-gradient-to-r from-gold-500 to-gold-600 text-luxury-black font-['Montserrat'] font-semibold text-sm py-3 rounded-lg flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-secure-payment-line"></i>
                Pagar com cart\u00e3o
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tela de seleção de método
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6 overflow-y-auto">
      <div className="bg-luxury-dark border border-gold-500/30 rounded-2xl max-w-lg w-full p-8 my-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center">
              <i className="ri-vip-crown-2-fill text-white text-lg"></i>
            </div>
            <div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-white">Upgrade Premium</h3>
              <p className="font-['Montserrat'] text-xs text-white/40">Receba sua carta por email</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors cursor-pointer">
            <i className="ri-close-line text-white/60 text-xl"></i>
          </button>
        </div>

        <div className="w-full h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent mb-8"></div>

        {/* Benefícios */}
        <div className="bg-gold-500/5 border border-gold-500/15 rounded-xl p-5 mb-8">
          <p className="font-['Montserrat'] text-xs text-gold-400 uppercase tracking-wider mb-4 font-semibold">
            O que voc\u00ea ganha com o Premium
          </p>
          <div className="space-y-3">
            {[
              'Receba a carta por email na data',
              'Notifica\u00e7\u00e3o por WhatsApp',
              'Certificado digital de jornada',
              'Carta com design exclusivo',
              'Lembrete 30 dias antes',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gold-500/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xs text-gold-400"></i>
                </div>
                <span className="font-['Montserrat'] text-base text-white/70">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Valor */}
        <div className="text-center mb-8">
          <p className="font-['Montserrat'] text-xs text-white/40 mb-1">Pagamento \u00fanico</p>
          <p className="font-['Playfair_Display'] text-4xl text-gold-400 font-bold">R$ 97<span className="text-lg text-white/40">,00</span></p>
        </div>

        {/* CPF/CNPJ */}
        <div className="mb-6">
          <label className="font-['Montserrat'] text-xs text-white/50 mb-2 block tracking-wide">CPF ou CNPJ *</label>
          <input
            type="text"
            value={cpfCnpj}
            onChange={handleCpfChange}
            maxLength={18}
            placeholder="000.000.000-00"
            className="w-full bg-white/5 border border-gold-500/20 focus:border-gold-500 rounded-lg px-4 py-3 font-['Montserrat'] text-base text-white outline-none transition-colors placeholder:text-white/20"
          />
        </div>

        {/* Métodos de pagamento */}
        <div className="mb-8">
          <label className="font-['Montserrat'] text-xs text-white/50 mb-3 block tracking-wide">M\u00e9todo de pagamento *</label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: 'pix', nome: 'PIX', icon: 'ri-qr-code-line' },
              { id: 'boleto', nome: 'Boleto', icon: 'ri-barcode-line' },
              { id: 'cartao', nome: 'Cart\u00e3o', icon: 'ri-bank-card-line' },
            ].map((metodo) => (
              <button
                key={metodo.id}
                type="button"
                onClick={() => { setSelectedMetodo(metodo.id); setError(''); }}
                className={`rounded-xl p-4 text-center transition-all duration-300 cursor-pointer border ${
                  selectedMetodo === metodo.id
                    ? 'bg-gold-500/15 border-gold-500/50 scale-105'
                    : 'bg-white/[0.03] border-gold-500/10 hover:border-gold-500/30'
                }`}
              >
                <i className={`${metodo.icon} text-2xl ${selectedMetodo === metodo.id ? 'text-gold-400' : 'text-white/40'} block mb-2`}></i>
                <span className={`font-['Montserrat'] text-base ${selectedMetodo === metodo.id ? 'text-gold-400 font-semibold' : 'text-white/50'}`}>
                  {metodo.nome}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Erro */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
            <p className="font-['Montserrat'] text-xs text-red-400">
              <i className="ri-error-warning-line mr-1"></i>{error}
            </p>
          </div>
        )}

        {/* Botão */}
        <button
          onClick={handleUpgrade}
          disabled={isProcessing}
          className="w-full bg-gradient-to-r from-gold-500 to-gold-700 text-white font-['Montserrat'] font-semibold text-sm py-4 rounded-lg hover:shadow-[0_0_30px_rgba(201,168,76,0.3)] transition-all duration-500 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed tracking-wider uppercase"
        >
          {isProcessing ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Processando...
            </>
          ) : (
            <>
              <i className="ri-vip-crown-2-fill"></i>
              Fazer upgrade agora
            </>
          )}
        </button>

        <p className="font-['Montserrat'] text-xs text-white/30 text-center mt-4 flex items-center justify-center gap-1">
          <i className="ri-shield-check-line text-gold-500/50"></i>
          Pagamento 100% seguro
        </p>
      </div>
    </div>
  );
};

export default UpgradeModal;
